import { friendlyCrmSyncError } from "@/lib/crmSyncError";
import { useEffect, useState } from "react";
import { Drawer } from "@/components/organisms/Drawer";
import { AddressPreview } from "@/components/molecules/AddressPreview";
import { useResource } from "@/hooks/useResource";
import { leadsService } from "@/services/leadsService";
import { contactsService } from "@/services/contactsService";
import { branchesService } from "@/services/branchesService";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Badge } from "@/components/atoms/Badge";
import { CopyButton } from "@/components/atoms/CopyButton";
import { useSettingsStore } from "@/store/settingsStore";
import { cn } from "@/lib/cn";
import { formatDateAbsolute, formatEnquiryDateParts } from "@/utils/date-helpers";
import { FileText, User, ListPlus, Settings, ShieldAlert, MapPin } from "lucide-react";



function EnquiryDateCell({ value }: { value: string | null | undefined }) {
  const parts = formatEnquiryDateParts(value);
  if (!parts) return <span className="text-bridge-subtle/50">—</span>;
  return (
    <div className="leading-tight tabular-nums flex flex-col gap-0.5">
      <div className="font-medium text-bridge-fg">{parts.date}</div>
      <div className="text-xs text-bridge-muted">{parts.time}</div>
    </div>
  );
}

function getLeadName(data: any) {
  if (!data) return "Unnamed Lead";
  if (data.enquirerName) return data.enquirerName;
  const parts = [data.enquirerForename, data.enquirerSurname].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  return "Unnamed Lead";
}

interface FieldProps {
  label: string;
  value: React.ReactNode;
  colSpan?: 1 | 2;
  copyValue?: string | null;
  copyLabel?: string;
}

function Field({ label, value, colSpan = 1, copyValue, copyLabel = label }: FieldProps) {
  const isEmpty = value === "—" || value === null || value === undefined || (typeof value === "string" && !value.trim());
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <div className="text-[10px] font-semibold text-bridge-subtle uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className="group/copy flex min-w-0 items-center gap-1">
        <div className={cn(
          "min-w-0 text-sm font-medium leading-relaxed",
          isEmpty ? "text-bridge-subtle/40 font-normal" : "text-bridge-fg"
        )}>
          {isEmpty ? "—" : value}
        </div>
        <CopyButton value={copyValue} label={copyLabel} />
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

function Section({ title, icon, children }: SectionProps) {
  return (
    <section className="bg-white border border-bridge-border rounded-xl p-5 shadow-card transition-all duration-200 hover:shadow-soft">
      <div className="flex items-center gap-2 border-b border-bridge-border/80 pb-3 mb-4">
        {icon}
        <h3 className="text-xs font-bold text-bridge-fg tracking-wider uppercase">
          {title}
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-y-4 gap-x-6">
        {children}
      </div>
    </section>
  );
}

interface Props {
  open: boolean;
  leadId: string | null;
  onClose: () => void;
}

const TONE_BY_STATUS: Record<string, "info" | "success" | "warning" | "danger" | "neutral" | "accent"> = {
  NEW: "info",
  QUALIFIED: "success",
  WON: "success",
  CONTACTED: "warning",
  LOST: "danger",
  UNKNOWN: "accent",
};

export function LeadDetailDrawer({ open, leadId, onClose }: Props) {
  const { legalEntityId } = useSettingsStore();
  const { data, loading, error } = useResource(
    () => (leadId ? leadsService.getOne(leadId) : Promise.resolve(null)),
    [leadId, open],
  );

  const [crmSyncStatus, setCrmSyncStatus] = useState<string | null>(null);
  const [crmSyncError, setCrmSyncError] = useState<string | null>(null);
  const [branchName, setBranchName] = useState<string | null>(null);

  useEffect(() => {
    const contactRefId = data?.contactRefId != null ? String(data.contactRefId).trim() : "";
    if (contactRefId) {
      contactsService.getOne(contactRefId).then((res) => {
        setCrmSyncStatus(res.crmSyncStatus || res.syncAlto || null);
        setCrmSyncError(res.crmSyncError || null);
      }).catch(() => {
        setCrmSyncStatus(null);
        setCrmSyncError(null);
      });
    } else {
      setCrmSyncStatus(null);
      setCrmSyncError(null);
    }
  }, [data?.contactRefId]);

  useEffect(() => {
    const externalBranchId = data?.externalBranchId != null ? String(data.externalBranchId).trim() : "";
    if (!open || !externalBranchId || !legalEntityId) {
      setBranchName(null);
      return;
    }

    let cancelled = false;
    branchesService
      .list({ legalEntityId }, { page: 0, size: 200 })
      .then((res) => {
        if (cancelled) return;
        const match = (res.content ?? []).find(
          (b) => (b.externalId != null ? String(b.externalId).trim() : "") === externalBranchId || b.id === externalBranchId,
        );
        setBranchName(match?.name?.trim() || null);
      })
      .catch(() => {
        if (!cancelled) setBranchName(null);
      });

    return () => {
      cancelled = true;
    };
  }, [open, data?.externalBranchId, legalEntityId]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={getLeadName(data) !== "Unnamed Lead" ? getLeadName(data) : (data ? "Unnamed Lead" : "Lead Details")}
      width="md"
    >
      {loading && !data && (
        <div className="space-y-6">
          <Skeleton className="h-28 w-full rounded-xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-36 w-full rounded-xl" />
        </div>
      )}

      {error && (
        <div className="p-4 bg-bridge-danger/10 text-bridge-danger border border-bridge-danger/20 rounded-xl flex items-center gap-3">
          <ShieldAlert size={20} />
          <span className="text-sm font-medium">Failed to load lead details. Please try again.</span>
        </div>
      )}

      {!loading && data && (
        <div className="space-y-6">
          <Section title="Overview" icon={<FileText size={16} className="text-bridge-accent" />}>
            <Field
              label="Status"
              value={
                data.leadStatus ? (
                  <Badge
                    tone={TONE_BY_STATUS[String(data.leadStatus)] ?? "neutral"}
                    className={
                      String(data.leadStatus).trim().toUpperCase() === "UNKNOWN"
                        ? "rounded-full px-2.5"
                        : undefined
                    }
                  >
                    {data.leadStatus}
                  </Badge>
                ) : "—"
              }
            />
            <Field label="Enquiry Type" value={data.enquiryType} />
            <Field label="Enquiry Date" value={<EnquiryDateCell value={data.enquiryDate} />} />
          </Section>

          <Section title="Enquirer Details" icon={<User size={16} className="text-bridge-accent" />}>
            <Field label="Name" value={getLeadName(data) !== "Unnamed Lead" ? getLeadName(data) : "—"} />
            <Field label="Email" value={data.enquirerEmail} copyValue={data.enquirerEmail} />
            <Field label="Mobile Phone" value={data.enquirerMobilePhone} copyValue={data.enquirerMobilePhone} copyLabel="Phone number" />
            <Field label="Home Phone" value={data.enquirerHomePhone} copyValue={data.enquirerHomePhone} copyLabel="Phone number" />
          </Section>

          <Section title="Address" icon={<MapPin size={16} className="text-bridge-accent" />}>
            <div className="col-span-2">
              <AddressPreview
                addressSubDwelling={data.enquirerHouseName}
                addressNameNumber={data.enquirerHouseNumber}
                street={data.enquirerStreet}
                locality={data.enquirerLocality}
                town={data.enquirerTown || data.enquirerArea}
                county={data.enquirerCounty}
                postcode={data.enquirerPostcode}
              />
            </div>
          </Section>

          <Section title="Additional Details" icon={<ListPlus size={16} className="text-bridge-accent" />}>
            <Field label="Lead Source" value={data.leadSource} />
            <Field label="Marketing Consent" value={data.marketingConsent} />
            <Field label="Position" value={data.position} />
            <Field label="Bedrooms" value={data.bedrooms} />
            <Field
              label="Buying Price"
              value={
                data.buyingPriceFrom || data.buyingPriceTo
                  ? `£${data.buyingPriceFrom || "0"} - £${data.buyingPriceTo || "Any"}`
                  : "—"
              }
            />
            <Field
              label="Rent"
              value={
                data.rentFrom || data.rentTo
                  ? `£${data.rentFrom || "0"} - £${data.rentTo || "Any"}${
                      data.rentFrequency ? " (" + data.rentFrequency + ")" : ""
                    }`
                  : "—"
              }
            />
            <Field label="Property IDs" value={data.propertyIds} colSpan={2} />
            <Field
              label="Message"
              value={data.message ? <div className="whitespace-pre-wrap">{data.message}</div> : "—"}
              colSpan={2}
            />
            <Field
              label="Comment"
              value={data.comment ? <div className="whitespace-pre-wrap">{data.comment}</div> : "—"}
              colSpan={2}
            />
          </Section>

          <Section title="System Info" icon={<Settings size={16} className="text-bridge-accent" />}>
            <Field label="Source CRM" value={data.sourceCrm} />
            <Field label="CRM Lead ID" value={data.externalId} />
            <Field
              label="Branch"
              value={
                branchName || data.externalBranchId != null ? (
                  <Badge tone="neutral">{branchName || String(data.externalBranchId)}</Badge>
                ) : "—"
              }
            />
            <Field label="User ID" value={data.externalUserId} />
            <Field label="CRM Contact ID" value={data.contactRefExternalId} copyValue={data.contactRefExternalId} />
            <Field label="Atlas Contact ID" value={data.contactRefId} />
            <Field label="Contact Sync Status" value={crmSyncStatus} />
            <Field
              label="Contact Sync Error"
              value={crmSyncError ? <span className="text-bridge-danger break-words">{friendlyCrmSyncError(crmSyncError)}</span> : "—"}
              colSpan={2}
            />
            <Field
              label="Reachout Error"
              value={data.reachoutError ? <span className="text-bridge-danger break-words">{data.reachoutError}</span> : "—"}
              colSpan={2}
            />
            <Field label="Created Date (Atlas)" value={formatDateAbsolute(data.atlasCreatedAt)} />
            <Field
              label="Last Updated (Atlas)"
              value={formatDateAbsolute(data.atlasModifiedAt)}
            />
          </Section>
        </div>
      )}
    </Drawer>
  );
}

